import { useEffect, useRef } from 'react';
import {
  initSession,
  syncSession,
  trackEnterLevel,
  trackGameOverAttempt,
  trackSolvedAttempt,
} from '@/analytics/analytics';
import { useGameStore } from '@/state/gameStore';
import { selectBezelState, selectCowardStatus, selectFlag, selectSpiteMode, selectUltimateCoward } from '@/state/selectors';
import type { LevelId } from '@/state/types';
import { sameLevelId } from '@/state/levelFlow';

function isGameOver(id: LevelId): boolean {
  return id.kind === 'GAME_OVER';
}

function debugEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('sc-analytics-debug') === '1';
  } catch {
    return false;
  }
}

function dbg(msg: string, data?: unknown): void {
  if (!debugEnabled()) return;
   
  console.info(`[analytics-hook] ${msg}`, data ?? '');
}

/**
 * Centralises analytics wiring. Inserts exactly one `level_attempts` row per
 * ended attempt (solve or game over). Level-specific notes can be patched
 * via `trackAttemptNote()` from within a level component.
 */
export function useAnalytics(): void {
  const didInitRef = useRef(false);

  // Read store fields via selectors to avoid full-store subscriptions.
  const cowardStatus = useGameStore(selectCowardStatus);
  const ultimateCoward = useGameStore(selectUltimateCoward);
  const spiteMode = useGameStore(selectSpiteMode);
  const falseEndingShown = useGameStore(selectFlag('falseEndingShown'));
  const bezelState = useGameStore(selectBezelState);
  const tokens = useGameStore((s) => s.tokens);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    dbg('mount: initSession()');
    void initSession({
      cowardStatus,
      ultimateCoward,
      spiteMode,
      flags: { falseEndingShown },
      bezelState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync milestone + bezel state changes (best-effort, fire-and-forget).
  useEffect(() => {
    dbg('syncSession()');
    syncSession({
      cowardStatus,
      ultimateCoward,
      spiteMode,
      flags: { falseEndingShown },
      bezelState,
    });
  }, [bezelState, cowardStatus, falseEndingShown, spiteMode, ultimateCoward]);

  // Attempt tracking: subscribe to level transitions and flush on solve/game over.
  useEffect(() => {
    // Use the always-available subscribe(listener) form (no subscribeWithSelector needed).
    let didFirst = false;
    const unsubscribe = useGameStore.subscribe((next, prev) => {
      const current = next.currentLevel;
      const previous = prev.currentLevel;

      // First observation: treat it as entering the current level.
      if (!didFirst) {
        didFirst = true;
        dbg('first state seen', { current, tokens: next.tokens });
        if (!isGameOver(current)) trackEnterLevel(current, next.tokens);
        return;
      }

      if (sameLevelId(previous, current)) return;
      dbg('level transition', { from: previous, to: current, tokens: next.tokens });

      const endedAtIso = new Date().toISOString();

      // Any transition into GAME_OVER ends the previous attempt as game_over.
      if (!isGameOver(previous) && isGameOver(current)) {
        queueMicrotask(() => {
          trackGameOverAttempt(0, endedAtIso);
        });
        return;
      }

      // Any transition from non-GAME_OVER -> non-GAME_OVER is considered a solve.
      if (!isGameOver(previous) && !isGameOver(current)) {
        queueMicrotask(() => {
          trackSolvedAttempt(next.tokens, endedAtIso);
          trackEnterLevel(current, next.tokens);
        });
        return;
      }

      // Transition out of GAME_OVER into a level (retry/reset) starts a new attempt.
      if (isGameOver(previous) && !isGameOver(current)) {
        trackEnterLevel(current, next.tokens);
      }
    });

    return unsubscribe;
  }, []);

  // This hook uses selectors for most fields, but tokens is referenced for init
  // reads; keep the React render subscribed so we don't capture stale values.
  void tokens;
}

